import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, ApplicationConfig, LOCALE_ID, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CropEffects, cropReducer } from '@next/arable-lib/crop';
import { CropFieldEffects, cropFieldReducer } from '@next/arable-lib/crop-field';
import {
  GeoBrowserService,
  GeoCalculationServiceInjector,
  GeoModule,
  GeoServiceInjector,
  InteractionServicesInjector,
  MapFeaturePopUpInjector,
} from '@next/arable-lib/geo';
import { CoreEffects } from '@next/core-lib';
import { coreRoutes, provideAgrCore } from '@next/core-lib/app';
import { I18nBrowserEffects, provideAgrBrowser } from '@next/core-lib/browser';
import { AgrNavigationRailModule, provideAgrLocationSummary } from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { provideAgrSettings } from '@next/core-lib/settings';
import { provideAgrWrapperSiteSidenav } from '@next/core-lib/wrapper-site-sidenav';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { provideState, provideStore, StoreFeatureModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { AgrBrandingModule } from '@next/core-lib/branding';
import { provideAgrCart } from '@next/core-lib/cart';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { I18nEffects, provideAgrI18nShared } from '@next/core-lib/i18n';
import { NavigationEffects, provideAgrNavigation } from '@next/core-lib/navigation';
import { AgrPWAModule } from '@next/core-lib/pwa';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { SiteEffects, provideAgrSite } from '@next/core-lib/site';
import { provideAgrTable } from '@next/core-lib/table';
import { AdviceEffects } from './advices/store/effects';
import { advicesFeature } from './advices/store/feature';
import { appRoutes } from './app.routes';
import { BfpEffects } from './bem/store/bfp.effects';
import { bfpFeature } from './bem/store/bfp.feature';
import { BlightEffects } from './blight-v2/store/effects';
import { MapFeaturePopupProvider } from './dashboard/components/cropfield-map/components/map-feature-popup-provider';
import { GeoSelectBlightService } from './dashboard/components/cropfield-map/layers/map.blight.layer';
import { GeoSelectCropFieldService } from './dashboard/components/cropfield-map/layers/map.cropField.layer';
import { GeoSoilAnalysisLayer } from './dashboard/components/cropfield-map/layers/map.soilAnalysis.layer';
import { MapEffects } from './dashboard/components/cropfield-map/store/map.effects';
import { mapReducer } from './dashboard/components/cropfield-map/store/map.reducers';
import { EnvironmentalImpactEffects } from './dashboard/components/environmental-impact/store/environmental-impact.effects';
import { environmentalImpactFeature } from './dashboard/components/environmental-impact/store/environmental-impact.reducers';
import { OrganicMatterBalanceEffects } from './dashboard/components/organic-matter-balance/store/organic-matter-balance.effects';
import { organicMatterBalanceFeature } from './dashboard/components/organic-matter-balance/store/organic-matter-balance.reducers';
import { loadedConfig } from './runtime-config';
import { AutomaticAdviceNotificationEffects } from './shared/automatic-advice/store/automatic-advice-notifcation.effects';
import { TgaAccessGuard } from './shared/guards/tga-access.guard';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { JsonDateInterceptor } from './shared/interceptors/jsondateinterceptor.interceptor';
import { LocationInterceptor } from './shared/interceptors/location.interceptor';
import { SignalStoreCustomerInterceptor } from './shared/interceptors/signal-store-customer.interceptor';
import { AppGeoCalculationBrowserService } from './shared/services/app-geo-calculation-browser.service';
import { SoilTypeEffects } from './soil-type/store/soil-type.effects';
import { soilTypeFeature } from './soil-type/store/soil-type.feature';
import { AppEffects } from './store/effects';
import { CropYearEffects } from './store/crop-year.effects';
import { appReducer } from './store/reducer';
import { TgaEffects } from './tga/store/tga.effects';
import { tgaFeature } from './tga/store/tga.feature';

interface MapContextFallbackState {
  popupEnabled: boolean;
  mapType: number;
}

const initialMapContextFallbackState: MapContextFallbackState = {
  popupEnabled: true,
  mapType: 0,
};

const mapContextFallbackReducer = (
  state: MapContextFallbackState = initialMapContextFallbackState,
): MapContextFallbackState => state;

const patchGeoModuleStoreFeatureImport = (): void => {
  const injectorDef = (GeoModule as { ɵinj?: { imports?: unknown[] } }).ɵinj;
  if (!injectorDef?.imports || !Array.isArray(injectorDef.imports)) {
    return;
  }

  injectorDef.imports = injectorDef.imports.filter((importDef) => {
    const ngModule = (importDef as { ngModule?: unknown })?.ngModule ?? importDef;
    return ngModule !== StoreFeatureModule;
  });
};

patchGeoModuleStoreFeatureImport();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([...appRoutes, ...coreRoutes], withEnabledBlockingInitialNavigation()),
    provideStore({
      'map-context': mapContextFallbackReducer,
    }, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),
    provideState('app', appReducer),
    provideState(advicesFeature),
    provideState(bfpFeature),
    provideState('map', mapReducer),
    provideState(environmentalImpactFeature),
    provideState(organicMatterBalanceFeature),
    provideState(soilTypeFeature),
    provideState(tgaFeature),
    provideState('crops', cropReducer),
    provideState('crop-field', cropFieldReducer),
    provideEffects([
      // Core-lib effects (must be registered by the consuming app)
      CoreEffects,
      I18nEffects,
      I18nBrowserEffects,
      SiteEffects,
      NavigationEffects,
      // App-specific effects
      AppEffects,
      CropYearEffects,
      BlightEffects,
      AdviceEffects,
      AutomaticAdviceNotificationEffects,
      BfpEffects,
      MapEffects,
      EnvironmentalImpactEffects,
      OrganicMatterBalanceEffects,
      SoilTypeEffects,
        TgaEffects,
      CropEffects,
      CropFieldEffects,
    ]),
    provideAgrRouterStore(),
    provideAgrBrowser(),
    provideAgrSite(),
    provideAgrCore(),
    provideAgrCart(),
    provideAgrTable(),
    provideAgrWrapperSiteSidenav(),
    provideAgrElasticApi(),
    provideAgrI18nShared(),
    // Icon initialization for LocationSummary component
    provideAgrLocationSummary(),
    // Settings service and store
    provideAgrSettings(),
    // AdvisorRelationshipsSelector service
    provideAgrAdvisorRelationshipsSelector(),
    // Site and Navigation state
    provideAgrNavigation(),
    provideAppInitializer(async () => {
      const featureFlagService = inject(FeatureFlagService);
      featureFlagService.initialize('dashboard');
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: false,
    }),
    importProvidersFrom(EffectsModule.forRoot([])),
    importProvidersFrom(AgrBrandingModule),
    importProvidersFrom(AgrNavigationRailModule),
    importProvidersFrom(AgrPWAModule),
    { provide: GeoServiceInjector, useClass: GeoBrowserService },
    { provide: GeoCalculationServiceInjector, useClass: AppGeoCalculationBrowserService },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectCropFieldService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSelectBlightService,
      multi: true,
    },
    {
      provide: InteractionServicesInjector,
      useClass: GeoSoilAnalysisLayer,
      multi: true,
    },
    {
      provide: MapFeaturePopUpInjector,
      useClass: MapFeaturePopupProvider,
    },
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => loadedConfig(),
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LocationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SignalStoreCustomerInterceptor, multi: true },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: false, strict: false },
    },
    {
      provide: MAT_DATE_LOCALE,
      useExisting: LOCALE_ID,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS,
    },
    TgaAccessGuard,
  ],
};
